import { useState } from 'react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogScrollArea,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CheckCircle, 
  Copy, 
  Download, 
  ExternalLink, 
  Eye, 
  EyeOff,
  FileText,
  Play,
  Settings,
  Code,
  Globe,
  Shield,
  Zap,
  Terminal
} from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { useTranslation } from 'react-i18next';

interface BareAPIConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: {
    integration_name: string;
    binding_id: string;
    auth_config: {
      api_key: string;
      endpoint_url?: string;
      auth_method?: string;
      headers?: Record<string, string>;
    };
    permissions: {
      can_read: boolean;
      can_write: boolean;
      can_search: boolean;
      can_delete: boolean;
      filters?: any;
    };
  };
  spaceName: string;
}

export function BareAPIConfigModal({
  isOpen,
  onClose,
  integration,
  spaceName
}: BareAPIConfigModalProps) {
  const { t } = useTranslation();
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState<'api_key' | 'config' | 'instructions' | 'curl_example' | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'config' | 'instructions'>('overview');

  const handleCopy = async (content: string, type: 'api_key' | 'config' | 'instructions' | 'curl_example') => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getPermissionsList = () => {
    const permissions = [];
    if (integration.permissions.can_read) permissions.push(t('bareAPIConfig.permissions.readMemories', { ns: 'integrations' }) as string);
    if (integration.permissions.can_write) permissions.push(t('bareAPIConfig.permissions.writeMemories', { ns: 'integrations' }) as string);
    if (integration.permissions.can_search) permissions.push(t('bareAPIConfig.permissions.searchMemories', { ns: 'integrations' }) as string);
    if (integration.permissions.can_delete) permissions.push(t('bareAPIConfig.permissions.deleteMemories', { ns: 'integrations' }) as string);
    return permissions;
  };

  const getAPIEndpoint = () => {
    const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000').replace(
      /\/$/,
      ''
    );
    return `${baseUrl}/api/v1/socket-access/${integration.binding_id}`;
  };

  const getConfigurationJSON = () => {
    return JSON.stringify({
      integration_name: integration.integration_name,
      binding_id: integration.binding_id,
      api_endpoint: getAPIEndpoint(),
      auth_config: {
        api_key: integration.auth_config.api_key,
        auth_method: integration.auth_config.auth_method || 'bearer_token',
        headers: integration.auth_config.headers || {}
      },
      permissions: integration.permissions,
      space_name: spaceName
    }, null, 2);
  };

  const getCURLExample = () => {
    return `# Test API Connection
curl -X GET "${getAPIEndpoint()}/info" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}" \\
  -H "Content-Type: application/json"

# Create Memory
curl -X POST "${getAPIEndpoint()}/memories" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content": "This is a test memory",
    "tags": ["test", "api"],
    "category": "general",
    "importance": 5
  }'

# Search Memories
curl -X POST "${getAPIEndpoint()}/memories/search" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "query": "test memory",
    "limit": 10
  }'`;
  };

  const getSetupInstructions = () => {
    return `# Bare API Integration Setup Instructions

## Overview
Your Bare API integration provides direct REST API access to your Memara memory system. This integration allows you to programmatically create, read, search, and manage memories through standard HTTP requests.

## API Endpoint
Base URL: \`${getAPIEndpoint()}\`

## Authentication
Use Bearer token authentication with your API key:
\`Authorization: Bearer ${integration.auth_config.api_key}\`

## Available Endpoints

### 1. Get Integration Info
\`GET /info\`
Returns integration details and permissions.

### 2. Memory Operations
- \`GET /memories\` - List memories (if can_read permission)
- \`POST /memories\` - Create new memory (if can_write permission)
- \`GET /memories/{id}\` - Get specific memory (if can_read permission)
- \`DELETE /memories/{id}\` - Delete memory (if can_delete permission)

### 3. Search Operations
- \`GET /memories/search?query=example&limit=10\` - Simple search with query parameters
- \`POST /memories/search\` - Advanced search with JSON request body (supports filters)

## Request Examples

### Create Memory
\`\`\`bash
curl -X POST "${getAPIEndpoint()}/memories" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content": "Important information to remember",
    "tags": ["important", "reference"],
    "category": "work",
    "importance": 8
  }'
\`\`\`

### Search Memories (Simple - GET)
\`\`\`bash
curl -X GET "${getAPIEndpoint()}/memories/search?query=important%20information&limit=10" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}"
\`\`\`

### Search Memories (Advanced - POST)
\`\`\`bash
curl -X POST "${getAPIEndpoint()}/memories/search" \\
  -H "Authorization: Bearer ${integration.auth_config.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "query": "important information",
    "limit": 10,
    "filter_tags": ["important"],
    "filter_source": "work"
  }'
\`\`\`

## Response Format
All responses are in JSON format with standard HTTP status codes:
- \`200\` - Success
- \`201\` - Created
- \`400\` - Bad Request
- \`401\` - Unauthorized
- \`404\` - Not Found
- \`500\` - Server Error

## Permissions
Your integration has the following permissions:
${getPermissionsList().map(p => `- ${p}`).join('\n')}

## Best Practices
1. Always include the Authorization header
2. Use appropriate HTTP methods (GET, POST, DELETE)
3. Include Content-Type: application/json for POST requests
4. Handle errors gracefully with proper status code checking
5. Use tags and categories for better memory organization
6. Set appropriate importance levels (1-10) for memories

## Rate Limiting
- 100 requests per minute per integration
- 1000 requests per hour per integration
- Respect rate limit headers in responses

## Security Notes
- Keep your API key secure and never expose it in client-side code
- Use HTTPS in production environments
- Rotate API keys periodically
- Monitor API usage for unusual patterns

Need help? Contact our support team or check the documentation.`;
  };

  const downloadInstructions = () => {
    const blob = new Blob([getSetupInstructions()], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${integration.integration_name.toLowerCase().replace(/\s+/g, '-')}-setup-instructions.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadConfig = () => {
    const blob = new Blob([getConfigurationJSON()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${integration.integration_name.toLowerCase().replace(/\s+/g, '-')}-config.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent variant="scrollable" className="max-w-5xl bg-white">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Settings className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-xl text-gray-900">{integration.integration_name} Configuration</div>
              <div className="text-sm text-gray-600 font-normal">
                Manage your Bare API integration settings and access credentials
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="flex min-h-0 flex-1 flex-col w-full">
          <TabsList className="grid w-full grid-cols-3 flex-shrink-0 mx-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="instructions" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Instructions
            </TabsTrigger>
          </TabsList>

          <DialogScrollArea className="px-6">
          <TabsContent value="overview" className="mt-6">
            <div className="space-y-6">
              {/* Integration Details */}
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-blue-900">
                    <Globe className="w-5 h-5" />
                    Integration Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700 font-medium">Integration Name:</span>
                      <div className="text-blue-900">{integration.integration_name}</div>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Connected Space:</span>
                      <div className="text-blue-900">{spaceName}</div>
                    </div>
                    <div className="md:col-span-2">
                      <span className="text-blue-700 font-medium">API Endpoint:</span>
                      <div className="bg-blue-100 rounded-lg p-3 font-mono text-xs break-all mt-1 overflow-x-auto">
                        {getAPIEndpoint()}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => handleCopy(getAPIEndpoint(), 'config')}
                      >
                        {copied === 'config' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        Copy Endpoint
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* API Key Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    API Authentication
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-700">API Key:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm flex-1 break-all overflow-x-auto">
                        {showApiKey ? integration.auth_config.api_key : '••••••••••••••••••••••••••••••••'}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => handleCopy(integration.auth_config.api_key, 'api_key')}
                    >
                      {copied === 'api_key' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      Copy API Key
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Permissions Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Permissions & Access
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {getPermissionsList().map((permission) => (
                      <Badge key={permission} variant="outline" className="bg-green-100 text-green-800 border-green-300">
                        {permission}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      onClick={() => handleCopy(getCURLExample(), 'curl_example')}
                      className="flex items-center gap-2"
                    >
                      {copied === 'curl_example' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      Copy cURL Examples
                    </Button>
                    <Button
                      variant="outline"
                      onClick={downloadConfig}
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Config
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => window.open('/integrations/bare-api', '_blank')}
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Documentation
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="config" className="mt-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5" />
                    Integration Configuration JSON
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-w-full">
                    <pre className="text-gray-100 text-sm whitespace-pre-wrap break-words">
                      <code>{getConfigurationJSON()}</code>
                    </pre>
                  </div>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => handleCopy(getConfigurationJSON(), 'config')}
                  >
                    {copied === 'config' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    Copy Configuration
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Terminal className="w-5 h-5" />
                    cURL Examples
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-w-full">
                    <pre className="text-gray-100 text-sm whitespace-pre-wrap break-words">
                      <code>{getCURLExample()}</code>
                    </pre>
                  </div>
                  <Button
                    variant="outline"
                    className="mt-3"
                    onClick={() => handleCopy(getCURLExample(), 'curl_example')}
                  >
                    {copied === 'curl_example' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    Copy Examples
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="instructions" className="mt-6">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Setup Instructions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto max-w-full">
                    <pre className="text-sm whitespace-pre-wrap text-gray-800 break-words">
                      {getSetupInstructions()}
                    </pre>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => handleCopy(getSetupInstructions(), 'instructions')}
                      className="flex items-center gap-2"
                    >
                      {copied === 'instructions' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      Copy Instructions
                    </Button>
                    <Button
                      variant="outline"
                      onClick={downloadInstructions}
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download Instructions
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          </DialogScrollArea>
        </Tabs>

        <DialogFooter variant="scrollable" className="justify-between px-6 pb-6">
          <div className="flex gap-2">
            <Button
              onClick={() => window.open('/integrations/bare-api', '_blank')}
              variant="outline"
              size="sm"
            >
              <FileText className="w-4 h-4 mr-1" />
              Documentation
            </Button>
            <Button
              onClick={() => window.open(`${getAPIEndpoint()}/info`, '_blank')}
              variant="outline"
              size="sm"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Test API
            </Button>
          </div>
          <Button onClick={onClose} className="bg-blue-600 hover:bg-blue-700">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 